import type { ReactNode } from 'react';
import React from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TurnRightIcon from '@mui/icons-material/TurnRight';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Typography from '@mui/material/Typography';

import { useField } from 'app-components/flux/transaction-rules/transactionRulesFormHooks';
import { useStateContext } from 'app-components/flux/transaction-rules/transactionRulesFormState';
import type { TransactionRulesStages } from 'app-components/flux/transaction-rules/transactionRulesTypes';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
  drawer: {
    width: 'calc(100% - 75px)',
    flexShrink: 0,
    zIndex: theme.zIndex.modal,
  },
  drawerPaper: {
    maxHeight: '100%',
    width: 'calc(100% - 75px)',
    overflow: 'hidden',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    flex: 1,
  },
  header: {
    display: 'flex',
    flex: 0,
    justifyContent: 'flex-start',
    padding: `${theme.spacing(2)} ${theme.spacing(4)}`,
    borderBottom: `1px solid ${theme.palette.border.main}`,
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingBottom: theme.spacing(1.75),
  },
  instructions: {
    display: 'flex',
    background: theme.palette.background.paper,
    borderRadius: theme.spacing(1.5),
    padding: `${theme.spacing(1.5)} ${theme.spacing(2)}`,
    boxShadow: theme.shadows[10],
    '> svg': {
      height: 20,
      transform: `rotate(90deg)`,
    },
  },
  spacer: {
    width: 140,
    minHeight: theme.spacing(5.625),
  },
  content: {
    display: 'flex',
    flex: '1 0 auto',
    flexDirection: 'column',
    padding: `${theme.spacing(2)} ${theme.spacing(5)} 0 `,
    backgroundColor: theme.palette.background.default,
    maxHeight: `calc(100% - 154px)`,
    overflow: 'auto',
  },
  footer: {
    display: 'flex',
    flex: 0,
    justifyContent: 'flex-end',
    padding: `${theme.spacing(2)} ${theme.spacing(4)} ${theme.spacing(3)}`,
    borderTop: `1px solid ${theme.palette.border.main}`,
  },
  backNav: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    width: 140,
  },
  backNavText: {
    fontWeight: 500,
  },
}));

interface CustomDrawerProps {
  id: string;
  open: boolean;
  stage: TransactionRulesStages;
  onClose: () => void;
  header: ReactNode;
  children: ReactNode;
  footer: ReactNode;
  backText: string;
}

const SettingsDrawer: React.FC<CustomDrawerProps> = ({
  id,
  open,
  stage,
  onClose,
  header,
  children,
  footer,
  backText,
}) => {
  const { state } = useStateContext();
  const { rule } = state;
  const { classes } = useStyles();

  const { value: hasSource } = useField('isEnabledPayeeSource'); // always true for Phase 1

  // TODO: After Phase 1, there is is going to be more to check here
  const instructionText = hasSource ? (
    <>
      <Typography variant='paragraphRegular'>Add THEN conditions that you want your rule to change.</Typography>
      <TurnRightIcon />
    </>
  ) : (
    <>
      <TurnRightIcon />
      <Typography variant='paragraphRegular'>Add IF conditions that will target transactions.</Typography>
    </>
  );

  return (
    <Drawer
      id={id}
      className={classes.drawer}
      anchor='right'
      open={open}
      onClose={onClose}
      classes={{
        paper: classes.drawerPaper,
      }}
    >
      <Box className={classes.container}>
        <Box className={classes.header}>{header}</Box>
        <Box className={classes.content}>
          <Box className={classes.headerRow}>
            <Box className={classes.backNav} onClick={onClose} id='settings-drawer-backnav'>
              <ArrowBackIcon />
              <Typography className={classes.backNavText} variant='bodyRegular'>
                {backText}
              </Typography>
            </Box>
            {stage === 'VALIDATED' ? (
              <Box className={classes.spacer}></Box>
            ) : (
              rule && !rule.id && <Box className={classes.instructions}>{instructionText}</Box>
            )}
            <Box className={classes.spacer}></Box>
          </Box>
          {children}
        </Box>
        <Box className={classes.footer}>{footer}</Box>
      </Box>
    </Drawer>
  );
};

export default SettingsDrawer;
